import { Role, User } from "../models/User";
import { Client, Group, Organization, PermissionEditDTO } from "../models/ControlPanel";
import { ColorVariables } from "../utils/uiVariables";
import client from "./axios";
import { NavigationSection, SectionKey } from "../components/layout/AppSidebar";

const controlPanelUrl = `${window.__env.GATEWAY_URL}/v1/control-panel`;

interface RolesRespose {
  clientMappings: any;
  realmMappings: Role[];
}

export interface SectionLabels {
  singular?: string;
  plural?: string;
  title: string;
}

export type SectionListDTO = Omit<NavigationSection, "items" | "subSection"> & {
  key: SectionKey; // window.__env.NAVIGATION_SECTIONS[key]
  items?: Omit<SectionListDTO, "items">[];
}

export interface UserStatus {
  keycloak: {
    attributes: boolean;
    organizations: boolean;
    permissions: boolean;
  };
  catalogue: boolean;
  minio: boolean;
}

export const ControlPanelManager = {
  getUsers: (params?: URLSearchParams) => client.get<User[]>(`${controlPanelUrl}/users`, { params }),
  getUser: (id: string) => client.get<User>(`${controlPanelUrl}/users/${id}`),
  getUserOrganizations: (id: string) => client.get<Organization[]>(`${controlPanelUrl}/users/${id}/organizations`),
  changeOrganization: (id: string, organization: Organization) => client.put<User>(`${controlPanelUrl}/users/${id}/change-organization`, organization),
  createUser: (user: User) => client.post<User>(`${controlPanelUrl}/users`, user),
  updateUser: (user: User) => client.put<User>(`${controlPanelUrl}/users/${user.id}`, user),
  getUserStatus: (id: string) => client.get<UserStatus>(`${controlPanelUrl}/users/${id}/status`),
  fixUser: (user: User) => client.post<User>(`${controlPanelUrl}/users/${user.id}/fix`, user),
  updateUserPassword: (id: string, body: any) => client.put<User>(`${controlPanelUrl}/users/${id}/password`, body),
  deleteUser: (id: string) => client.delete(`${controlPanelUrl}/users/${id}`),
  getClients: (params?: URLSearchParams) => client.get<Client[]>(`${controlPanelUrl}/clients`, { params }),
  getClientRoles: (clientId: string, params?: URLSearchParams) => client.get(`${controlPanelUrl}/clients/${clientId}/roles`, { params }),
  getOrganizations: () => client.get(`${controlPanelUrl}/organizations`),
  createOrganization: (organization: Organization) => client.post(`${controlPanelUrl}/organizations`, organization),
  updateOrganization: (organization: Organization) => client.put(`${controlPanelUrl}/organizations/${organization.id}`, organization),
  getPermissions: () => client.get(`${controlPanelUrl}/permissions`),
  createPermission: (permission: PermissionEditDTO) => client.post(`${controlPanelUrl}/permissions`, permission),
  updatePermission: (permission: PermissionEditDTO) => client.put(`${controlPanelUrl}/permissions/${permission.id}`, permission),
  getRoles: () => client.get<Role[]>(`${controlPanelUrl}/roles`),
  createRole: (role: Role) => client.post<Role>(`${controlPanelUrl}/roles`, role),
  deleteRole: (id: string) => client.delete(`${controlPanelUrl}/roles/${id}`),
  getGroups: () => client.get<Group[]>(`${controlPanelUrl}/groups`),
  getGroup: (id: string) => client.get<Group>(`${controlPanelUrl}/groups/${id}`),
  getGroupMembers: (id: string) => client.get<User[]>(`${controlPanelUrl}/groups/${id}/members`),
  createGroup: (group: Organization) => client.post<Organization>(`${controlPanelUrl}/groups`, group),
  deleteGroup: (id: string) => client.delete(`${controlPanelUrl}/groups/${id}`),
  getUserGroups: (id: string) => client.get<Group[]>(`${controlPanelUrl}/users/${id}/groups`),
  addUserToGroup: (userId: string, groupId: string) => client.post(`${controlPanelUrl}/users/${userId}/groups/${groupId}`),
  removeUserFromGroup: (userId: string, groupId: string) => client.delete(`${controlPanelUrl}/users/${userId}/groups/${groupId}`),
  getUserRoles: (id: string) => client.get<RolesRespose>(`${controlPanelUrl}/users/${id}/role-mappings`),
  getUserClientRoles: (id: string, clientId: string) => client.get<Role[]>(`${controlPanelUrl}/users/${id}/role-mappings/clients/by-client-id/${clientId}`),
  addRolesToUser: (userId: string, roles: Role[]) => client.post(`${controlPanelUrl}/users/${userId}/role-mappings`, roles),
  removeRolesFromUser: (userId: string, roles: Role[]) => client.delete(`${controlPanelUrl}/users/${userId}/role-mappings`, { data: roles }),
  updateStyleVariables: (variables: ColorVariables) => client.post(`${controlPanelUrl}/customization/styles/variables`, variables),
  getStylesFile: () => client.get(`${controlPanelUrl}/customization/styles`),
  uploadStylesFile: (file: FormData) => client.post(`${controlPanelUrl}/customization/styles`, file),
  getEnvFile: () => client.get(`${controlPanelUrl}/customization/env`),
  uploadEnvFile: (file: FormData) => client.post(`${controlPanelUrl}/customization/env`, file),
  updateImage: (image: FormData) => client.post(`${controlPanelUrl}/customization/images`, image),
  getSections: (params?: URLSearchParams) => client.get<Array<SectionListDTO> | Record<SectionKey, NavigationSection>>(`${controlPanelUrl}/customization/env/sections`, { params }),
  updateSections: (body: SectionListDTO[]) => client.put(`${controlPanelUrl}/customization/env/sections`, body),
}